/**
 * [INPUT]: 依赖调用方注入的持久化存储、服务端 access/refresh token 响应与时间源
 * [OUTPUT]: 对外提供 Authorization 会话 token 解析、持久化、过期判断和跨页面 refresh 单飞锁
 * [POS]: 跨 Web 客户端共享的认证传输基础设施；不依赖 React、Cookie 或具体运行平台，Web 端注入 localStorage，原生 App 继续使用 SecureStore
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */

export type AuthTokens = {
  accessToken: string;
  refreshToken: string;
  accessExpiresAt: string;
  refreshExpiresAt?: string;
};

export interface TokenStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const ACCESS_REFRESH_SKEW_MS = 90_000;
const REFRESH_LOCK_TTL_MS = 30_000;
const REFRESH_LOCK_WAIT_MS = 35_000;
const REFRESH_LOCK_POLL_MS = 50;

type RefreshLease = {
  owner: string;
  expiresAt: number;
};

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** 从 JWT payload 兜底推导 access token 的绝对过期时间。 */
export function jwtAccessExpiresAt(token: string): string {
  try {
    const payload = token.split('.')[1];
    if (!payload) return '';
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=');
    const decoded = JSON.parse(globalThis.atob(normalized)) as { exp?: unknown };
    const seconds = typeof decoded.exp === 'number' ? decoded.exp : Number(decoded.exp);
    return Number.isFinite(seconds) ? new Date(seconds * 1000).toISOString() : '';
  } catch {
    return '';
  }
}

/** 将服务端 snake_case 会话响应收敛为各 Web 客户端共用的 token 模型。 */
export function parseAuthTokens(value: unknown): AuthTokens | null {
  const source = record(value);
  const accessToken = stringValue(source.access_token ?? source.accessToken);
  const refreshToken = stringValue(source.refresh_token ?? source.refreshToken);
  if (!accessToken || !refreshToken) return null;

  return {
    accessToken,
    refreshToken,
    accessExpiresAt: stringValue(source.expires_at ?? source.access_expires_at ?? source.accessExpiresAt) || jwtAccessExpiresAt(accessToken),
    ...(stringValue(source.refresh_expires_at ?? source.refreshExpiresAt)
      ? { refreshExpiresAt: stringValue(source.refresh_expires_at ?? source.refreshExpiresAt) }
      : {}),
  };
}

export class BearerTokenStore {
  private readonly owner = `refresh-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  constructor(
    private readonly storage: TokenStorage,
    private readonly tokenKey: string,
    private readonly lockKey: string,
    private readonly now: () => number = () => Date.now(),
  ) {}

  get(): AuthTokens | null {
    try {
      const raw = this.storage.getItem(this.tokenKey);
      if (!raw) return null;
      const parsed = parseAuthTokens(JSON.parse(raw));
      return parsed?.accessExpiresAt ? parsed : parsed ? { ...parsed, accessExpiresAt: jwtAccessExpiresAt(parsed.accessToken) } : null;
    } catch {
      return null;
    }
  }

  set(tokens: AuthTokens): void {
    this.storage.setItem(this.tokenKey, JSON.stringify(tokens));
  }

  clear(expectedRefreshToken?: string): boolean {
    const current = this.get();
    if (expectedRefreshToken && current?.refreshToken !== expectedRefreshToken) return false;
    if (!current && expectedRefreshToken) return false;
    this.storage.removeItem(this.tokenKey);
    return true;
  }

  isAccessTokenExpiring(tokens = this.get(), skewMs = ACCESS_REFRESH_SKEW_MS): boolean {
    if (!tokens?.accessExpiresAt) return false;
    const expiresAt = Date.parse(tokens.accessExpiresAt);
    return Number.isFinite(expiresAt) && expiresAt <= this.now() + skewMs;
  }

  /**
   * 用 localStorage lease 协调多个页面的 refresh 请求。
   * 现代浏览器没有 navigator.locks 时仍可工作；崩溃页面留下的 lease 会自动过期。
   */
  async withRefreshLock<T>(work: () => Promise<T>): Promise<T> {
    const deadline = this.now() + REFRESH_LOCK_WAIT_MS;
    while (this.now() < deadline) {
      const current = this.readLease();
      if (!current || current.expiresAt <= this.now()) {
        const lease: RefreshLease = { owner: this.owner, expiresAt: this.now() + REFRESH_LOCK_TTL_MS };
        this.writeLease(lease);
        if (this.readLease()?.owner === this.owner) {
          try {
            return await work();
          } finally {
            this.releaseLease();
          }
        }
      }
      await new Promise<void>((resolve) => globalThis.setTimeout(resolve, REFRESH_LOCK_POLL_MS));
    }
    throw new Error('AUTH_REFRESH_LOCK_TIMEOUT');
  }

  private readLease(): RefreshLease | null {
    try {
      const raw = this.storage.getItem(this.lockKey);
      if (!raw) return null;
      const value = JSON.parse(raw) as Partial<RefreshLease>;
      return typeof value.owner === 'string' && typeof value.expiresAt === 'number' ? value as RefreshLease : null;
    } catch {
      return null;
    }
  }

  private writeLease(lease: RefreshLease): void {
    try {
      this.storage.setItem(this.lockKey, JSON.stringify(lease));
    } catch {
      // 存储不可用时让调用方收到锁超时，不能悄悄假装完成跨页面协调。
    }
  }

  private releaseLease(): void {
    try {
      if (this.readLease()?.owner === this.owner) this.storage.removeItem(this.lockKey);
    } catch {
      // lease 会自然过期，不把清理失败升级为认证失败。
    }
  }
}
